export interface ApiProblem {
  title: string;
  status: number;
  code: string;
  detail?: string;
  violations?: Array<{ code: string; field?: string; detail?: string }>;
  correlationId?: string;
}

export class ApiError extends Error {
  constructor(
    readonly problem: ApiProblem,
  ) {
    super(problem.title);
    this.name = 'ApiError';
  }
}

const API_BASE = '/api/v1';

let accessToken: string | null = null;
let tokenLoader: (() => string | null) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function registerTokenLoader(loader: () => string | null): void {
  tokenLoader = loader;
}

export function createRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function correlationId(): string {
  return createRequestId();
}

function authHeader(): Record<string, string> {
  const token = accessToken ?? tokenLoader?.() ?? null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch a file rather than JSON.
 *
 * apiFetch always parses the body as JSON, which is exactly wrong for a
 * download — it would consume the workbook and throw. This shares the auth and
 * correlation headers and hands back the bytes plus the server's filename.
 *
 * The filename comes from Content-Disposition, which the browser only exposes
 * because the API lists it in the CORS exposedHeaders; the fallback matters
 * when that is misconfigured, because the alternative is a file called after
 * the URL path.
 */
export async function apiDownload(
  path: string,
  init: RequestInit = {},
  fallbackFilename = 'download',
): Promise<{ blob: Blob; filename: string }> {
  const headers = new Headers(init.headers);
  headers.set('X-Correlation-Id', correlationId());
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  for (const [key, value] of Object.entries(authHeader())) {
    headers.set(key, value);
  }

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });

  if (!response.ok) {
    // An error response is still JSON, so the normal problem-detail path works.
    const payload = (await response.json().catch(() => null)) as ApiProblem | null;
    throw new ApiError(
      payload ?? { title: response.statusText, status: response.status, code: 'DOWNLOAD_FAILED' },
    );
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob: await response.blob(), filename: match?.[1] ?? fallbackFilename };
}

/**
 * Hand a blob to the browser as a save.
 *
 * The object URL is revoked on the next tick rather than immediately — Safari
 * cancels the download if the URL dies before it has started reading.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  options?: { idempotencyKey?: string },
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('X-Correlation-Id', correlationId());
  if (options?.idempotencyKey) {
    headers.set('Idempotency-Key', options.idempotencyKey);
  }
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  for (const [key, value] of Object.entries(authHeader())) {
    headers.set(key, value);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json().catch(() => null)) as ApiProblem | null;
  if (!response.ok) {
    throw new ApiError(payload ?? {
      title: response.statusText,
      status: response.status,
      code: 'INTERNAL_ERROR',
    });
  }

  return payload as T;
}

export function violationMessage(problem: ApiProblem): string {
  const first = problem.violations?.[0];
  if (!first) return problem.detail ?? problem.title;
  if (first.code === 'ACCOUNT_NOT_REGISTERED') {
    return first.detail ?? 'Account not registered';
  }
  if (first.code === 'MOBILE_ALREADY_IN_USE') {
    return first.detail ?? 'This mobile number is already linked to another account.';
  }
  if (first.code === 'MOBILE_REQUIRED') {
    return first.detail ?? 'Add a mobile number before continuing.';
  }
  if (first.code === 'NO_ACTIVE_OFFERING') {
    return 'Registration is not yet available for this selection. Please check back later or contact support.';
  }
  if (first.code === 'SELECTION_INVALID') {
    return 'Please select at least one subject before requesting an estimate.';
  }
  if (first.code === 'LEVEL_NOT_OFFERED') {
    return 'The selected grade is not available for this school and year.';
  }
  if (first.code === 'QUOTATION_ALREADY_PAID') {
    return 'This fee is already captured on another payment for the same quotation.';
  }
  if (first.code === 'QUOTATION_EXPIRED') {
    return 'Your fee quotation has expired. Refreshing with an updated amount…';
  }
  if (first.code === 'ORDER_ALREADY_CAPTURED') {
    return 'This payment has already been captured.';
  }
  return first.detail ?? problem.title;
}

export function apiErrorMessage(problem: ApiProblem): string {
  if (problem.code === 'DEPENDENCY_UNAVAILABLE') {
    return problem.title ?? 'A required service is temporarily unavailable. Please try again shortly.';
  }
  return violationMessage(problem);
}

export function violationFieldErrors(
  problem: ApiProblem,
): Partial<Record<string, string>> {
  const errors: Partial<Record<string, string>> = {};
  for (const violation of problem.violations ?? []) {
    if (!violation.field) continue;
    errors[violation.field] = violation.detail ?? violationMessage({
      ...problem,
      violations: [violation],
    });
  }
  return errors;
}
